import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, query, orderBy, where, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { Search, Edit, Trash2, Plus, Upload, CheckSquare, Square } from 'lucide-react';
import ExcelImport from './ExcelImport';

const SalesTable = ({ onEdit, onAddNew }) => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [farmers, setFarmers] = useState([]);
  const [selectedFarmer, setSelectedFarmer] = useState('');
  const [flops, setFlops] = useState([]);
  const [selectedFlop, setSelectedFlop] = useState('');

  useEffect(() => {
    fetchFarmers();
    fetchFlops();
    fetchSales();
  }, []);

  const fetchFarmers = async () => {
    try {
      const q = query(collection(db, 'farmers'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      setFarmers(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching farmers:', error);
    }
  };

  const fetchFlops = async () => {
    try {
      const q = query(collection(db, 'flops'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      setFlops(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching flops:', error);
    }
  };

  const fetchSales = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'sales'), orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);
      const salesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSales(salesData);
    } catch (error) {
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await deleteDoc(doc(db, 'sales', id));
        setSales(sales.filter(sale => sale.id !== id));
        setSelectedItems(selectedItems.filter(itemId => itemId !== id));
      } catch (error) {
        console.error('Error deleting record:', error);
        alert('Error deleting record. Please try again.');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one record to delete.');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedItems.length} selected record(s)?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const batch = writeBatch(db);
      
      // Add delete operations to batch
      selectedItems.forEach(id => {
        const docRef = doc(db, 'sales', id);
        batch.delete(docRef);
      });

      // Execute batch
      await batch.commit();

      // Update local state
      setSales(sales.filter(sale => !selectedItems.includes(sale.id)));
      setSelectedItems([]);
      setSelectAll(false);

      alert(`Successfully deleted ${selectedItems.length} record(s).`);
    } catch (error) {
      console.error('Error deleting records:', error);
      alert('Error deleting records. Please try again.');
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
    } else {
      const allFilteredIds = filteredSales.map(sale => sale.id);
      setSelectedItems(allFilteredIds);
      setSelectAll(true);
    }
  };

  const handleSelectItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
      setSelectAll(false);
    } else {
      setSelectedItems([...selectedItems, id]);
      // Check if all filtered items are now selected
      if (selectedItems.length + 1 === filteredSales.length) {
        setSelectAll(true);
      }
    }
  };

  const handleImportSuccess = () => {
    fetchSales(); // Refresh the data after import
  };

  // Filter sales by selected farmer and flop
  const filteredSales = sales.filter(sale => {
    if (!selectedFarmer || !selectedFlop) return false; // Show nothing by default
    const farmerMatch = selectedFarmer === 'ALL' ? true : sale.farmerId === selectedFarmer;
    const flopMatch = selectedFlop === 'ALL' ? true : sale.flopId === selectedFlop;
    return farmerMatch && flopMatch;
  }).filter(sale => {
    const searchLower = searchTerm.toLowerCase();
    return (
      sale.farmerName?.toLowerCase().includes(searchLower) ||
      sale.flopName?.toLowerCase().includes(searchLower) ||
      sale.date?.toLowerCase().includes(searchLower) ||
      sale.delivered?.toString().includes(searchLower) ||
      sale.trayJama?.toString().includes(searchLower) ||
      sale.eggTray?.toString().includes(searchLower) ||
      sale.ogRate?.toString().includes(searchLower) ||
      sale.rate?.toString().includes(searchLower) ||
      sale.totalPrice?.toString().includes(searchLower) ||
      sale.paymentJama?.toString().includes(searchLower) ||
      sale.paymentReceived?.toString().includes(searchLower)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatNumber = (number) => {
    return parseFloat(number || 0).toFixed(2);
  };

  const totalRevenue = filteredSales.reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);
  const totalDelivered = filteredSales.reduce((sum, sale) => sum + (sale.delivered || 0), 0);
  const totalEggTray = filteredSales.reduce((sum, sale) => sum + (sale.eggTray || 0), 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header with Search and Filters */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold text-gray-800">Sales Records</h2>
            <button
              onClick={onAddNew}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Plus size={16} />
              Add New Record
            </button>
            <button
              onClick={() => setShowImport(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <Upload size={16} />
              Import Excel
            </button>
            {selectedItems.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                <Trash2 size={16} />
                Delete Selected ({selectedItems.length})
              </button>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search by payment name or date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Date Filter */}
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        {/* Summary */}
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
          <span>Total Records: {filteredSales.length}</span>
          <span>Total Revenue: {formatCurrency(totalRevenue)}</span>
          <span>Total Delivered: {formatNumber(totalDelivered)}</span>
          <span>Total Egg Tray: {formatNumber(totalEggTray)}</span>
          {selectedItems.length > 0 && (
            <span className="text-blue-600 font-medium">
              Selected: {selectedItems.length} record(s)
            </span>
          )}
        </div>
      </div>

              {/* Farmer & Flop Filter Dropdowns */}
        <div className="mb-4 flex flex-wrap gap-4 items-center px-6">
          <label className="font-medium text-gray-700">Farmer:</label>
          <select
            value={selectedFarmer}
            onChange={e => setSelectedFarmer(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Farmer</option>
            <option value="ALL">All</option>
            {farmers.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        <label className="font-medium text-gray-700 ml-4">Flop:</label>
        <select
          value={selectedFlop}
          onChange={e => setSelectedFlop(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select Flop</option>
          <option value="ALL">All</option>
          {flops.map(f => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farmer</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flop</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flop ID</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivered</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tray Jama</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Egg Tray</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OG Rate</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Price</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Jama</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Received</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredSales.length === 0 ? (
              <tr>
                <td colSpan="14" className="px-4 py-12 text-center text-gray-500">
                  No sales records found
                </td>
              </tr>
            ) : (
              filteredSales.map((sale) => (
                <tr key={sale.id} className={`hover:bg-gray-50 ${selectedItems.includes(sale.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleSelectItem(sale.id)}
                      className="flex items-center justify-center w-5 h-5"
                    >
                      {selectedItems.includes(sale.id) ? (
                        <CheckSquare className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {sale.farmerName || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {sale.flopName || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-500">
                    {sale.flopId || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(sale.date)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatNumber(sale.delivered)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatNumber(sale.trayJama)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatNumber(sale.eggTray)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(sale.ogRate)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(sale.rate)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(sale.totalPrice)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(sale.paymentJama)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(sale.paymentReceived)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onEdit(sale)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(sale.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Excel Import Modal */}
      {showImport && (
        <ExcelImport
          onClose={() => setShowImport(false)}
          onSuccess={handleImportSuccess}
        />
      )}
    </div>
  );
};

export default SalesTable; 